<template>
  <div>
    <b-notification
        v-model="isError"
        type="is-danger"
        aria-close-label="Close notification"
        :closable="false">
        <b>Error fetching meal plan!</b><br />Try refreshing the page. If the error persists check your network connection or let BM know.
    </b-notification>
    <b-loading :is-full-page="true" v-model="isLoading"></b-loading>

    <!-- Sticky Top Bar with Breadcrumb -->
    <TopBar
      v-if="!isLoading && !isError"
      parentTitle="Meal Plans"
      :currentTitle="title || 'Loading...'"
      :backRoute="{ name: 'MealPlanListVIew' }"
    >
      <FeedsStatusBadge :status="mealplan.status" />
      <b-button outlined type="is-primary" size="is-small" icon-left="check" @click="saveAndClose">Save & Close</b-button>
      <b-button type="is-primary" size="is-small" icon-left="content-save" @click="save">Save</b-button>
    </TopBar>

    <div v-if="!isLoading && !isError" class="page-content">
      <Keypress key-event="keyup" :modifiers="['ctrlKey']" :key-code="83" :preventDefault="true" @success="save" />

      <!-- Page Header -->
      <div class="page-header">
        <div class="page-title-group">
          <h1 class="page-title">{{ title }}</h1>
          <p class="page-subtitle">{{ mealplan.date_string }}</p>
        </div>
      </div>

      <!-- Top Section: General Info + Images -->
      <div class="top-grid">
        <!-- Left Column: General Info -->
        <div class="form-column">
          <FeedsCard title="General Information">
            <template #header-actions>
              <b-select v-model="mealplan.status" placeholder="Status" size="is-small" @input="save">
                <option>draft</option>
                <option>test</option>
                <option>fix</option>
                <option>live</option>
              </b-select>
            </template>

            <div class="form-group">
              <label class="form-label">Meal Plan Name</label>
              <b-input v-model="mealplan.title" @input="saveDebounced" />
            </div>

            <div class="form-group">
              <label class="form-label">Description</label>
              <editor v-model="mealplan.description" @input="saveDebounced"/>
            </div>

            <div class="form-group">
              <label class="form-label">Date</label>
              <b-datepicker
                v-model="date"
                trap-focus
                placeholder="Click to select..."
                :unselectable-days-of-week="[0,1,2,3,5,6]"
                icon="calendar-today"
                :icon-right="mealplan.date ? 'close-circle' : ''"
                icon-right-clickable
                @icon-right-click="clearDate"
                @input="save" />
            </div>

            <div class="form-row" style="grid-template-columns: 1fr 1fr;">
              <div class="form-group">
                <label class="form-label">Background Colour</label>
                <div class="colour-select-wrapper">
                  <b-select expanded placeholder="Choose colour" icon="format-color-fill"
                    v-model="mealplan.backgroundColour"
                    @input="saveDebounced">
                    <option
                      v-for="option in colours"
                      :value="option"
                      :key="option.name">
                      {{ option.name }}
                    </option>
                  </b-select>
                  <b-button v-if="mealplan.backgroundColour != null" :style="backgroundStyle" type="is-light" size="is-small" icon-right="content-copy" @click="copyToClipboard(mealplan.backgroundColour.colour)">{{ mealplan.backgroundColour.colour }}</b-button>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">Highlight Colour</label>
                <div class="colour-select-wrapper">
                  <b-select expanded placeholder="Choose colour" icon="palette"
                    v-model="mealplan.highlightColour"
                    @input="saveDebounced">
                    <option
                      v-for="option in colours"
                      :value="option"
                      :key="option.name">
                      {{ option.name }}
                    </option>
                  </b-select>
                  <b-button v-if="mealplan.highlightColour != null" :style="highlightBackgroundStyle" type="is-light" size="is-small" icon-right="content-copy" @click="copyToClipboard(mealplan.highlightColour.colour)">{{ mealplan.highlightColour.colour }}</b-button>
                </div>
              </div>
            </div>

            <!-- Colour Preview -->
            <div class="form-group">
              <label class="form-label">Colour Preview</label>
              <div class="colour-preview-wrapper">
                <div class="colour-preview-card" :style="backgroundStyle">
                  <div class="preview-image-wrapper" v-if="image">
                    <img :src="image" class="preview-image" />
                  </div>
                  <div class="preview-text">
                    <h3 :style="backgroundStyle">{{ mealplan.title }}</h3>
                    <p :style="backgroundStyle">{{ this.mealplan.date_string }}</p>
                  </div>
                </div>
                <div class="colour-preview-highlight" :style="highlightBackgroundStyle"></div>
              </div>
            </div>

            <!-- Share Link -->
            <ShareLink
              v-model="mealplan.share_link"
              :selectedPath="`plan-${id}`"
              :destinationUrl="`https://feedsfeedsfeeds.com/?plan_id=${id}`"
              :linkName="`Plan: ${mealplan.title}`"
              @input="save"
            />
          </FeedsCard>
        </div>

        <!-- Right Column: Images -->
        <div class="preview-column">
          <FeedsCard title="Images">
            <template #header-actions>
              <b-button size="is-small" type="is-primary" icon-left="plus" @click="$refs.editImages.openAddImage()">Add Image</b-button>
            </template>
            <EditImages
              ref="editImages"
              v-model="mealplan.images"
              @input="save"
            />
          </FeedsCard>
        </div>
      </div>

      <!-- Tips and Recipes List Side by Side -->
      <div class="content-columns mt-5">
        <!-- Recipes Section -->
      <FeedsCard title="This Week's Recipes" class="mt-5">
        <template #header-actions>
          <b-button type="is-primary" size="is-small" icon-left="refresh" @click="refreshRecipes">Refresh</b-button>
        </template>
        <EditRecipes
          ref="editRecipes"
          title=""
          :maxRecipes="4"
          :allowMultipleFreeRecipes="false"
          v-model="mealplan.recipes"
          @input="save"
          @recipe-added="recipeAdded($event)"
          @recipe-removed="recipeRemoved($event)"
        />
      </FeedsCard>

      <!-- Tips Section -->
        <FeedsCard title="This Week's Top Tips">
          <EditTips
            v-model="mealplan.tips"
            title=""
            @input="save"
          />
        </FeedsCard>

      </div>

      

      <!-- Tags and Shopping List Side by Side -->
      <div class="content-columns mt-5">

        <!-- Shopping List Section -->
        <FeedsCard title="Shopping List">
          <template #header-actions>
            <b-button type="is-primary" size="is-small" icon-left="refresh" @click="refreshShoppingList">Refresh</b-button>
          </template>
          <IngredientList :ingredients="displayShoppingList" />
        </FeedsCard>

        <!-- Tags & Search Section -->
      <FeedsCard title="Tags & Search" class="mt-5">
        <EditTags
          v-model="mealplan.tags"
          entityType="meal_plans"
          @input="save"
        />
        <EditSeasonalAvailability
          v-model="mealplan.seasonal_availability"
          @input="save"
        />
      </FeedsCard>

      </div>

      <!-- Notes Section -->
      <FeedsCard title="Internal Notes" class="mt-5">
        <p class="form-hint mb-3">These notes are for internal use only and will not be visible to users.</p>
        <editor v-model="mealplan.notes" @input="saveDebounced"/>
      </FeedsCard>

      <!-- Newsletter Section -->
      <FeedsCard title="Newsletter Preview" class="mt-5">
        <template #header-actions>
          <b-button
            type="is-light"
            size="is-small"
            icon-left="eye"
            @click="showNewsletterHtml = !showNewsletterHtml"
          >{{ showNewsletterHtml ? 'Show Preview' : 'Show HTML' }}</b-button>
          <b-button
            type="is-primary"
            size="is-small"
            icon-left="content-copy"
            @click="copyToClipboard(newsletterHtml)">Copy HTML</b-button>
        </template>

        <div v-if="!showNewsletterHtml">
          <iframe
            :srcdoc="newsletterHtml"
            class="newsletter-preview"
          ></iframe>
        </div>
        <div v-else>
          <prism-editor
            v-model="newsletterHtml"
            :line-numbers="true"
            :highlight="highlighter"
            language="html"
            class="newsletter-editor"
          />
        </div>
      </FeedsCard>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import sortBy from 'lodash/sortBy'
import cloneDeep from 'lodash/cloneDeep'
import EditTips from "@/components/EditTips"
import EditImages from "@/components/EditImages"
import EditRecipes from "@/components/EditRecipes"
import Editor from "@/components/BaseEditor"
import EditTags from "@/components/EditTags"
import EditSeasonalAvailability from "@/components/EditSeasonalAvailability"
import FeedsCard from "@/components/common/FeedsCard"
import FeedsStatusBadge from "@/components/common/FeedsStatusBadge"
import TopBar from "@/components/layout/TopBar"
import db from "@/firebase/db"
import firebase from 'firebase/app'
import Colours from "@/models/Colours"
import DateFormatter from "@/formatters/DateFormatter"
import IngredientList from "@/components/IngredientList"
import ShareLink from "@/components/common/ShareLink"
import { copyToClipboard } from '@/helpers/clipboard'
import newsletterTemplate from '@/templates/newsletterTemplate'

// Prism editor for syntax highlighting
import { PrismEditor } from 'vue-prism-editor'
import { highlight, languages } from 'prismjs/components/prism-core';
import "prismjs/components/prism-markup";
import 'vue-prism-editor/dist/prismeditor.min.css'
import 'prismjs/themes/prism-twilight.css';

export default {
  name: 'MealPlanView',
  components: {
    EditImages,
    EditTips,
    EditRecipes,
    Editor,
    EditTags,
    EditSeasonalAvailability,
    FeedsCard,
    FeedsStatusBadge,
    TopBar,
    Keypress: () => import('vue-keypress'),
    ShareLink,
    IngredientList,
    PrismEditor
  },
  props: [
    'id'
  ],
  data() {
    return {
      isLoading: true,
      isError: false,
      mealplan: {},
      showNewsletterHtml: false,
    };
  },
  computed: {
    title() {
      if (this.mealplan.title == null) {
        return null;
      }
      return this.mealplan.title ?? "New Meal Plan"
    },
    image() {
      if (this.mealplan.images == null) {
        return null;
      }
      return this.mealplan.images.length > 0 ? this.mealplan.images[0].src : null
    },
    date: {
      get() {
        if (this.mealplan.date == null) {
          return null;
        }
        return new Date(this.mealplan.date)
      },
      set(val) {
        this.mealplan.date = val.getTime();
        this.mealplan.date_string = this.formatDate(this.mealplan.date);
      }
    },
    colours() {
      return Colours.all;
    },
    backgroundStyle() {
      if (this.mealplan.backgroundColour == null) {
        return { backgroundColor: Colours.white.colour }
      }
      return {
        backgroundColor: this.mealplan.backgroundColour.colour,
        color: (this.mealplan.backgroundColour.invertTheme == "dark") ? Colours.black.colour : Colours.white.colour
      }
    },
    highlightBackgroundStyle() {
      if (this.mealplan.highlightColour == null) {
        return { backgroundColor: Colours.black.colour }
      }
      return {
        backgroundColor: this.mealplan.highlightColour.colour,
        color: (this.mealplan.highlightColour.invertTheme == "dark") ? Colours.black.colour : Colours.white.colour
      }
    },
    highlightBodyStyle() {
      if (this.mealplan.highlightColour == null) {
        return { color: Colours.white.colour }
      }
      return { color: (this.mealplan.highlightColour.invertTheme == "dark") ? Colours.black.colour : Colours.white.colour }
    },
    pushPlanOutput() {
      return this.displayRecipes.map(function(recipe){
        return recipe.title;
      }).join(", ");
    },

    displayShoppingList() {
      if (this.mealplan.shopping_list == null) return [];

      let displayShoppingList = cloneDeep(this.mealplan.shopping_list);
      displayShoppingList = displayShoppingList.reduce(function(accumulator, current) {
        current.prep = "";

        let found = accumulator.find(function(item) {
          return item.name == current.name;
        });

        function convertToMls(found, current){
          if (found.unit == "tbsp") { found.amount = parseFloat(found.amount)*15 }
          if (current.unit == "tbsp"){ current.amount = parseFloat(current.amount)*15 }
          if (found.unit == "tsp") { found.amount = parseFloat(found.amount)*5 }
          if (current.unit == "tsp"){ current.amount = parseFloat(current.amount)*5 }

          found.unit = "ml";
          current.unit = "ml";
        }

        function convertToTbsp(found, current){
          if (found.unit == "tsp") { found.amount = parseFloat(found.amount)/3 }
          if (current.unit == "tsp"){ current.amount = parseFloat(current.amount)/3 }
          found.unit = "tbsp";
          current.unit = "tbsp";
        }

        function mergeIngredients(found, current){
          found.amount = parseFloat(found.amount);
          found.amount += +current.amount;
          found.tips = found.tips.concat(current.tips);
        }

        if (!found){
          accumulator.push(current);
        }else {
          if (found.unit == current.unit) {
            mergeIngredients(found, current);
          }else {
            switch (`${found.unit}-${current.unit}`){
              case ("tsp-tbsp"):
              case ("tbsp-tsp"):
                convertToTbsp(found, current);
                mergeIngredients(found, current);
                break;
              case ("tsp-ml"):
              case ("ml-tsp"):
              case ("tbsp-ml"):
              case ("ml-tbsp"):
                convertToMls(found, current);
                mergeIngredients(found, current);
                break;
              default:
                accumulator.push(current);
            }
          }
        }

        return accumulator;
      }, []);

      return sortBy(displayShoppingList, 'name');
    },
    displayRecipes() {
      var recipes = this.mealplan.recipes ?? {};

      var array = Object.keys(recipes).map(function(recipeID) {
        var newObj = {id: recipeID};
        for (var property in recipes[recipeID]) {
          newObj[property] = recipes[recipeID][property];
        }
        return newObj;
      });

      return array.sort((a, b) => a.position - b.position);
    },
    newsletterHtml() {
      return newsletterTemplate(this.mealplan, this.displayRecipes, this.mealplan.tips)
    },
  },
  watch: {
    newsletterHtml: {
      immediate: true,
      handler(val) {
        if (!this.showNewsletterHtml) {
          this.newsletterHtmlEditable = val;
        }
      }
    },
    showNewsletterHtml(val) {
      if (val) {
        this.newsletterHtmlEditable = this.newsletterHtml;
      }
    }
  },
  created: function () {
    db.collection('meal_plans')
      .doc(this.id)
      .get()
      .then(snapshot => {
        if (snapshot.exists) {
          this.mealplan = snapshot.data();
        }else {
          this.isError = true;
        }
        this.isLoading = false;
      })
      .catch((error) => {
        this.isError = true;
        this.$buefy.notification.open({type: 'is-danger', message: `Error fetching Meal plan: ${error}`})
      });
  },
  methods: {
    clearDate () {
      this.mealplan.date = null
    },
    formatDate(dateTime) {
      return DateFormatter.displayDate(dateTime, false);
    },
    saveDebounced: debounce(function () {
        this.save()
      }, 1000),
    async saveAndClose(){
      this.save()
        .then(() => {
          this.$router.go(-1)
        })
    },
    async save(){
      db.collection('meal_plans')
        .doc(this.id)
        .set(this.mealplan)
        .then(() => {
          this.$buefy.toast.open({type: 'is-success', message: `Meal plan successfully updated!`})
        })
        .catch((error) => {
          this.$buefy.toast.open({type: 'is-danger', message: `Error saving Meal plan: ${error}`})
        });
    },
    async recipeAdded(recipe){
      this.addRecipeIngredients(recipe);

      const entry = {};
      entry[`${this.id}`] = true;
      db.collection("recipe_mealplans").doc(recipe.id)
        .set(entry, {merge: true})
        .catch((error) => {
            console.error("Error adding recipe_mealplans entry: ", error);
        });
    },
    async recipeRemoved(recipe){
      this.removeRecipeIngredients(recipe);

      const remove = {};
      remove[`${this.id}`] = firebase.firestore.FieldValue.delete();
      db.collection("recipe_mealplans").doc(recipe.id)
        .update(remove)
        .catch((error) => {
            console.error("Error removing recipe_mealplans entry: ", error);
        });
    },
    async addRecipeIngredients(recipe) {
      db.collection('recipes')
        .doc(recipe.id)
        .get()
        .then(snapshot => {
          var ingredients = snapshot.data().ingredients;

          if (this.mealplan.shopping_list == null) {
            this.mealplan.shopping_list = [];
          }

          ingredients.forEach((item) => {
            item.recipe_id = recipe.id;
          });

          this.mealplan.shopping_list = this.mealplan.shopping_list.concat(ingredients);

          this.saveDebounced();
        })
    },
    async removeRecipeIngredients(recipe) {
      this.mealplan.shopping_list = this.mealplan.shopping_list.filter(item => item.recipe_id != recipe.id);
      this.saveDebounced();
    },
    async refreshShoppingList() {
      this.mealplan.shopping_list = [];

      Object.values(this.mealplan.recipes).forEach((recipe) => {
        this.addRecipeIngredients(recipe);
      });
    },
    refreshRecipes() {
      this.$refs.editRecipes.refreshRecipeList();
    },
    copyToClipboard(string){
      copyToClipboard(string);
    },
    highlighter(code) {
      return highlight(code, languages.html);
    }
  }
}
</script>

<style scoped>
.colour-select-wrapper {
  display: flex;
  gap: 8px;
  align-items: center;
}

.colour-preview-wrapper {
  display: flex;
  gap: 12px;
  align-items: stretch;
}

.colour-preview-card {
  border-radius: var(--radius-lg);
  overflow: hidden;
  padding: 16px;
}

.preview-image-wrapper {
  margin-bottom: 12px;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: var(--radius-md);
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-text h3 {
  font-family: 'Nunito', sans-serif;
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 4px;
}

.preview-text p {
  font-size: 13px;
  opacity: 0.9;
}

.colour-preview-highlight {
  width: 40px;
  border-radius: var(--radius-md);
}

.newsletter-preview {
  width: 100%;
  min-height: 800px;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  background: white;
}

.newsletter-editor {
  min-height: 600px;
  max-height: 800px;
  font-size: 13px;
  border-radius: var(--radius-md);
}

.content-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

@media (max-width: 768px) {
  .content-columns {
    grid-template-columns: 1fr;
  }

  .colour-preview-wrapper {
    flex-direction: column;
  }

  .colour-preview-highlight {
    width: 100%;
    height: 24px;
  }

  .colour-select-wrapper {
    flex-direction: column;
  }

  .newsletter-preview {
    min-height: 400px;
  }
}
</style>
